import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Icon, IconNames, ToastVariants } from '@dotcom/crown-ui';
import { AddToCart } from '../../components/add-to-cart';
import { AnimationPreviewDialog } from '../../components/animation-preview/animation-preview-dialog';
import { useAnimationPreview } from '../../components/animation-preview/hooks/use-animation-preview';
import { useUpdateAddress } from '../../components/address-form/hooks/use-update-address';
import { CardFaceToggle } from '../../components/card-face-toggle';
import { CardSelectBar } from '../../components/card-select-bar';
import { ECardLimitModal } from '../../components/ecard-limit-modal/ecard-limit-modal';
import { EditAddressDialog } from '../../components/edit-address-dialog/edit-address-dialog';
import { Layout } from '../../components/layout';
import { useGetAddresses } from '../../components/recipient-addresses-dialog/hooks/use-get-addresses';
import { RecipientAddressesDialog } from '../../components/recipient-addresses-dialog/recipient-addresses-dialog';
import { RecipientAddressesVariant } from '../../components/recipient-addresses-dialog/recipient-addresses-dialog-types';
import { SEO } from '../../components/seo/seo';
import { useAnalyticsContext } from '../../context/analytics-context';
import { hideLoadingScreen, setIsToasterOpen, showLoadingScreen, useAppContext } from '../../context/app-context';
import { setActiveCardIndex, useCardContext } from '../../context/card-context';
import { useInitializationDataContext } from '../../context/data-context';
import { AssetFormData, CardType } from '../../global-types';
import { AddressForm as AddressFormType } from '../../global-types/addresses';
import { EnvelopeType } from '../../global-types/envelope';
import { PreviewImage } from '../../global-types/images';
import { useBasket, useFeatureFlags, useLineItemUUID, useQueryParams } from '../../hooks';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { useSubmitOrder } from '../../hooks/use-submit-order';
import { getCurrentRegion } from '../../regional-config/get-regional-config';
import { saveRecipientAssets } from '../../services';
import { recipientsQueryOptions } from '../../services/recipients';
import { isLoggedIn, removeBeforeUnloadEvent, getMonolithUrl } from '../../utils';
import { useLoadCardAfterLogin } from '../address/hooks/use-load-card-after-login';
import { useMigrateAndLoadProject } from '../address/hooks/use-migrate-and-load-project';
import { populatePreviewImages } from '../address/utils';
import { useLoadExistingProject } from '../editor/hooks/use-load-existing-project';
import { useEditorNavigation } from '../editor/hooks/use-editor-navigation';
import { ConsumerRevewDialog } from './components/consumer-review-dialog/consumer-review-dialog';
import { EmailSplash, NoCrNoH } from './fragments';
import styles from './preview.module.scss';
import { finalizeOrder } from './utils';
import { useLoadPreviewOnFrontend } from './utils/load-preview-on-frontend';
import { PreviewImageCarousel } from './fragments/preview-image-carousel';
import { PreviewGenerationError } from './fragments/preview-generation-error';
import { usePreviewGeneration } from './hooks/use-preview-generation';

const INITIAL_SLIDE_INDEX = 0;

export const PreviewView = ({ defaultImages }: { defaultImages?: PreviewImage[] }): ReactElement => {
  const {
    initializedDataState,
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const {
    cardDispatch,
    cardState: { coverPreviewUrl, cardFacesList },
  } = useCardContext();
  const {
    appState: { isSystemErrorOpen, isMobileApp, previewImages },
    appDispatch,
  } = useAppContext();
  const {
    pageNavigationButtonClicked,
    trackLoadPreview,
    trackPreviewCard,
    trackReturnToEdit,
    trackDGPlayAnimation,
    trackSendEmailNow,
    trackSuccesfullySentEmail,
    trackDGViewFacePreview,
    trackClickPreviewCarouselImageTag,
  } = useAnalyticsContext();
  const { state: locationState } = useLocation();
  const { getPreviews } = useLoadPreviewOnFrontend();
  const { handleIndexChange } = useEditorNavigation();
  const isOneToMany = useIsOneToMany();
  const [slideIndex, setSlideIndex] = useState(INITIAL_SLIDE_INDEX);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // TODO: We can replace 'any' with new type when we stop using a mix of FE/BE previews
  const [images, setImages] = useState<PreviewImage[]>(defaultImages ?? []);
  const [btlyUrl, setBtlyUrl] = useState('');
  const queryClient = useQueryClient();
  const [isNoCrNoHOpen, setIsNoCrNoHOpen] = useState(false);
  const [isNoCrNoHLoyaltyVisible, setIsNoCrNoHLoyaltyVisible] = useState(false);
  const [isEcardLimitReachedOpen, setIsEcardLimitReachedOpen] = useState(false);
  const [consumerReviewCheck, setConsumerReviewCheck] = useState(false);
  const { IS_EDITOR_HEADER_ENABLED, IS_H_PLUS_ENABLED, IS_EDITOR_NAVIGATION_ENABLED } = useFeatureFlags();
  // we pass a lot of the basket data to the useSubmitOrder hook, but have to initialize it here to share the state with the hook
  const { addToCart, basket, recentBasket, basketError, closeModalOverride, keepShoppingOverride } = useBasket();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = useQueryParams();
  useLineItemUUID();
  const cardFacePreviewImages = useMemo(
    () =>
      images.map((image, index) => ({
        ...image,
        url: image.dataURL,
        dimensions: { width: 0, height: 0 },
        faceNumber: index + 1,
        photoZoneTemplate: '',
      })),
    [images],
  );
  const { handleSubmit } = useSubmitOrder(cardFacePreviewImages, setIsDialogOpen, addToCart);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  const { t } = useTranslation();
  const projectId = useMemo(() => initializedData?.project_id || '', [initializedData?.project_id]);
  const projectTypeCode = sessionStorage.getItem('lastProjectTypeCode');
  const isDigitalGreetings = projectTypeCode === CardType.DG;
  const quantity = queryParams.get('qty') ?? '1';
  const envelope = queryParams.get('envelope');
  const finalCtaMessage = isDigitalGreetings ? t('previewView.sendEmailNow') : t('previewView.addToCart');
  const continueMessage = isDigitalGreetings ? t('previewView.sendEmailNow') : t('previewView.addToCart');
  const playAnimationMessage = isMobileApp ? t('previewView.playAnimationMobile') : t('previewView.playAnimation');
  const region = getCurrentRegion();
  useMigrateAndLoadProject();
  const { loadExistingProject } = useLoadExistingProject();
  const { loadCardDataAfterLogin } = useLoadCardAfterLogin();
  const {
    animationPreviewImages,
    closeAnimation: handleCloseAnimationDialog,
    isAnimationDialogOpen,
    playAnimation: handlePlayAnimation,
  } = useAnimationPreview({
    projectId,
    onPlay: trackDGPlayAnimation,
  });

  const { mutate: sendEmailNow, isPending: isSendingemail } = useMutation({
    retry: false,
    mutationFn: async () => {
      const DGpreview = {
        name: 'Front',
        url: coverPreviewUrl,
      };

      showLoadingScreen(appDispatch, t('previewView.sendingEmailLoadingScreenTitle'));

      trackSendEmailNow(t('previewView.sendEmailNow'));
      // TODO: add call to send email here and get success response
      await finalizeOrder({
        cardPreviewImages: [DGpreview as any],
        btlyUrl: btlyUrl,
        onNoHPlusNonSubscriberError: () => {
          setIsNoCrNoHOpen(true);
          hideLoadingScreen(appDispatch);
        },
        onCrMemberLimitRechedError: () => {
          setIsEcardLimitReachedOpen(true);
          hideLoadingScreen(appDispatch);
        },
        onNonCrMemberError: () => {
          setIsNoCrNoHLoyaltyVisible(true);
          setIsNoCrNoHOpen(true);
          hideLoadingScreen(appDispatch);
        },
        onDefaultError: () => {
          setIsToasterOpen(appDispatch, {
            title: t('systemSendEmailNowError.title'),
            children: t('systemSendEmailNowError.description'),
            variant: ToastVariants.Error,
          });
        },
        initializedDataState,
        // TODO: Add feature flag here when we have it
        isHPlusEnabled: IS_H_PLUS_ENABLED,
        queryClient,
      });

      setShowEmailConfirmation(true);
      trackSuccesfullySentEmail();
    },
    onError: () => {
      setIsToasterOpen(appDispatch, {
        title: t('systemSendEmailNowError.title'),
        children: t('systemSendEmailNowError.description'),
        variant: ToastVariants.Error,
      });
    },
    onSettled: () => hideLoadingScreen(appDispatch),
  });
  const { mutate: saveDigitalAssets } = useMutation({
    retry: false,
    mutationFn: (assetFormData: AssetFormData) => saveRecipientAssets(projectId, assetFormData),
    onSuccess: (response) => setBtlyUrl(response.data?.url || ''),
    onError: () =>
      setIsToasterOpen(appDispatch, {
        title: t('editorView.previewGenerationErrorTitle'),
        children: t('editorView.previewGenerationError'),
        variant: ToastVariants.Error,
      }),
    onSettled: () => {
      hideLoadingScreen(appDispatch);
      return queryClient.invalidateQueries({ queryKey: recipientsQueryOptions(projectId).queryKey });
    },
  });

  const handleImageDotClick = (newIndex: number) => {
    const activeFace = cardFacesList[`${newIndex}`];
    trackClickPreviewCarouselImageTag(isEnvelope(newIndex), activeFace);
    setSlideIndex(newIndex);
  };

  const setAnalyticsIndex = (index: number) => {
    setSlideIndex(index);
    setActiveCardIndex(cardDispatch, index);
  };

  const isEnvelope = (index: number) => {
    return images[`${index}`].type === 'Envelope';
  };

  const handleNextButtonClick = async (index: number) => {
    if (index < images.length - 1) {
      pageNavigationButtonClicked.current = 'button';
      setAnalyticsIndex(index + 1);
      const activeFace = cardFacesList[`${index + 1}`];
      trackClickPreviewCarouselImageTag(isEnvelope(index + 1), activeFace);
    } else {
      if (isDigitalGreetings) {
        sendEmailNow();
      } else {
        setIsDialogOpen(true);
      }
    }
  };

  const handleFinalPreviewAction = () => {
    if (isDigitalGreetings) {
      sendEmailNow();
      return;
    }
    setIsDialogOpen(true);
  };

  const handlePrevClick = (index: number) => {
    const activeFace = cardFacesList[`${index - 1}`];
    trackClickPreviewCarouselImageTag(isEnvelope(index - 1), activeFace);
    if (index !== 0) {
      setAnalyticsIndex(index - 1);
    }
  };

  const handleChangeSlide = (newIndex: number) => {
    isDigitalGreetings && trackDGViewFacePreview(isEnvelope(newIndex));
    if (newIndex !== slideIndex) setAnalyticsIndex(newIndex);
  };

  const loadPreviewOnFrontend = useCallback(
    (generatedImages: PreviewImage[]) => {
      setImages(generatedImages);

      setActiveCardIndex(cardDispatch, 0);

      if (!isDigitalGreetings) {
        hideLoadingScreen(appDispatch);
      }

      if (locationState?.fromEdit) {
        trackPreviewCard();
      } else {
        trackLoadPreview();
      }
    },
    [cardDispatch, isDigitalGreetings, appDispatch, locationState?.fromEdit, trackPreviewCard, trackLoadPreview],
  );

  const {
    status: previewGenerationStatus,
    run: runPreviewGeneration,
    retry: retryPreviewGeneration,
  } = usePreviewGeneration({
    generate: getPreviews,
    onSuccess: loadPreviewOnFrontend,
    onError: () => hideLoadingScreen(appDispatch),
  });

  const handleRetryPreview = useCallback(() => {
    showLoadingScreen(appDispatch, '');
    retryPreviewGeneration();
  }, [appDispatch, retryPreviewGeneration]);

  const goBack = () => {
    trackReturnToEdit();
    navigate({ pathname: `/card/customization/edit/${projectId}`, search });
    handleIndexChange(0, 0);
  };

  const {
    isOpen: isRecipientDialogOpen,
    setIsOpen: setIsRecipientDialogOpen,
    isLoading: isGetAddressesLoading,
  } = useGetAddresses();

  // values and handlers for the edit address dialog
  const {
    formValues: formValuesPreview,
    handleUpdateReturnRecipientAddress,
    handleCloseEditAddress,
    dialogState: { openEditDialog, setOpenEditDialog },
  } = useUpdateAddress(setIsRecipientDialogOpen);
  const {
    handleSubmit: handleSubmitPreview,
    reset: resetRecipientAddressForm,
    formState: { isValid },
  } = formValuesPreview;

  const onRecipientAddressButtonClick = () => {
    setIsRecipientDialogOpen(false);
  };

  useEffect(() => {
    if (projectId) {
      // check that preview images have saved before loading them
      const previewImagesSaved = previewImages.length > 0;
      previewImagesSaved && void runPreviewGeneration();
    }
  }, [projectId, previewImages, runPreviewGeneration]);

  const lastProjectLoaded = sessionStorage.getItem('lastProjectLoaded');
  const isDataLoaded = useRef<boolean>(false);

  const loadProject = useCallback(async () => {
    showLoadingScreen(appDispatch, '');
    await loadExistingProject(lastProjectLoaded || '', isDataLoaded);
    hideLoadingScreen(appDispatch);
  }, [appDispatch, lastProjectLoaded, loadExistingProject]);

  useEffect(() => {
    if (initializedData === null) {
      if (isLoggedIn()) {
        loadCardDataAfterLogin();
      } else {
        loadProject();
      }
    } else {
      // If initilization data exists, check if we have preview images stored
      // If we don't, user reloaded. Populate state with preview images in assets data
      if (previewImages.length === 0 && initializedData?.assets && initializedData.assets.length > 0) {
        populatePreviewImages(initializedData, appDispatch, cardDispatch);
      }
    }
  }, [initializedData]);

  useEffect(() => {
    if (images.length >= 2 && isDigitalGreetings) {
      showLoadingScreen(appDispatch, 'Generating preview');

      const image1 = images[0];
      const image2 = images[1];

      saveDigitalAssets({
        front_url: image1.url ?? image1.dataURL,
        inside_url: image2.url ?? image2.dataURL,
      });
    }
  }, [images]);

  const hasPreviewSlides = images.length > 0;
  const isLastSlide = !(slideIndex < images.length - 1);
  const isFirstSlide = slideIndex === INITIAL_SLIDE_INDEX;
  const isHeaderPreviewCtaDisabled = !hasPreviewSlides || isSendingemail;

  const onEmailSplashContinue = () => {
    removeBeforeUnloadEvent();
    window.location.href = getMonolithUrl();
  };

  const onEmailSplashPlayAnimations = () => {
    handlePlayAnimation();
  };

  if (showEmailConfirmation) {
    return (
      <>
        <EmailSplash
          onContinue={onEmailSplashContinue}
          onPlayAnimations={onEmailSplashPlayAnimations}
          onCloseOverride={onEmailSplashContinue}
        />
        {isAnimationDialogOpen && (
          <AnimationPreviewDialog
            isOpen={isAnimationDialogOpen}
            panelImages={animationPreviewImages}
            onClose={handleCloseAnimationDialog}
          />
        )}
      </>
    );
  }

  const playAnimationButton = (
    <Button
      testId="play-animation-button"
      className={styles['animation-button-mobile']}
      onClick={handlePlayAnimation}
      disabled={!btlyUrl}
      variant="secondary"
    >
      <Icon name={IconNames.PlaysmallBold} />
      {playAnimationMessage}
    </Button>
  );

  return (
    <Layout
      hideToolbar
      previewHeaderProps={{
        onEditClick: goBack,
        ctaLabel: finalCtaMessage,
        onCtaClick: handleFinalPreviewAction,
        isCtaDisabled: isHeaderPreviewCtaDisabled,
      }}
    >
      <SEO title={`${t('previewView.title')}`} description={`${t('previewView.seo')}`} />
      {isDialogOpen && (
        <ConsumerRevewDialog
          region={region}
          isConsumerReviewDialogOpen={isDialogOpen}
          isConsumerReviewChecked={consumerReviewCheck}
          onConsumerDialogClose={() => setIsDialogOpen(false)}
          onActionButtonClick={() => handleSubmit(quantity)}
          onCancelButtonClick={() => setIsDialogOpen(false)}
          onCheckboxChange={(e) => setConsumerReviewCheck(e.target.checked)}
        />
      )}
      {IS_H_PLUS_ENABLED && isNoCrNoHOpen && (
        <NoCrNoH isOpen={isNoCrNoHOpen} setIsOpen={setIsNoCrNoHOpen} isLoyaltyCtaVisible={isNoCrNoHLoyaltyVisible} />
      )}
      {IS_H_PLUS_ENABLED && isEcardLimitReachedOpen && (
        <ECardLimitModal isOpen={isEcardLimitReachedOpen} onClose={() => setIsEcardLimitReachedOpen(false)} />
      )}
      {isAnimationDialogOpen && (
        <AnimationPreviewDialog
          isOpen={isAnimationDialogOpen}
          panelImages={animationPreviewImages}
          onClose={handleCloseAnimationDialog}
        />
      )}
      <div data-testid="preview-view" className={styles.preview}>
        <div className={styles['card-container']}>
          <div className={styles['prev-button']}>
            {!isFirstSlide && (
              <Button
                variant="secondary"
                size="icon"
                onClick={() => handlePrevClick(slideIndex)}
                aria-label="Last slide button"
                className={styles['button']}
                data-testid="prev-icon-button"
              >
                <Icon name={IconNames.ArrowsTailLeftBold} size={'10'} color="purple" className={styles['icon']} />
              </Button>
            )}
          </div>
          {previewGenerationStatus === 'error' ? (
            <PreviewGenerationError onRetry={handleRetryPreview} />
          ) : (
            <PreviewImageCarousel previewImages={images} slideIndex={slideIndex} onAfterSlide={handleChangeSlide} />
          )}
          <div className={styles['next-button']}>
            {!isLastSlide && (
              <Button
                size="icon"
                variant="secondary"
                onClick={() => handleNextButtonClick(slideIndex)}
                aria-label="Next slide button"
                className={styles['button']}
                data-testid="next-icon-button"
              >
                <Icon name={IconNames.ArrowsTailRightBold} size={'10'} color="purple" className={styles['icon']} />
              </Button>
            )}
          </div>
        </div>
        {slideIndex === images.length - 1 && isOneToMany && envelope === EnvelopeType.ReturnRecipient && (
          <Button
            className={styles['recipient-addresses-button']}
            variant="secondary"
            onClick={() => setIsRecipientDialogOpen(true)}
            size="large"
          >
            {t('recipientAddresses.title')}
            <Icon name={IconNames.AddressBookBold} color="purple" size="12" />
          </Button>
        )}
        {IS_EDITOR_NAVIGATION_ENABLED ? (
          <CardFaceToggle
            faces={cardFacePreviewImages}
            slideIndex={slideIndex}
            handleIndexChange={handleImageDotClick}
            mode="preview"
          />
        ) : (
          <CardSelectBar
            faces={cardFacePreviewImages}
            slideIndex={slideIndex}
            handleIndexChange={handleImageDotClick}
            mode="preview"
          />
        )}

        <div className={styles['play-button-mobile']}>{isDigitalGreetings && playAnimationButton}</div>

        <div className={styles['navigation-buttons']}>
          {!IS_EDITOR_HEADER_ENABLED && (
            <>
              <Button variant="secondary" onClick={goBack} className={styles['edit-button']}>
                {t('previewView.edit')}
              </Button>

              <div className={styles['action-buttons']}>
                {isDigitalGreetings &&
                  React.cloneElement(playAnimationButton, { className: styles['animation-button'] })}
                <Button
                  disabled={isSendingemail}
                  testId="next-button"
                  onClick={() => handleNextButtonClick(slideIndex)}
                >
                  {slideIndex < images.length - 1 ? t('previewView.next') : continueMessage}
                </Button>
              </div>
            </>
          )}

          {IS_EDITOR_HEADER_ENABLED && isDigitalGreetings && (
            <div className={styles['action-buttons']}>
              {React.cloneElement(playAnimationButton, { className: styles['animation-button'] })}
            </div>
          )}
        </div>

        {isRecipientDialogOpen && !isGetAddressesLoading && (
          <RecipientAddressesDialog
            isOpen={isRecipientDialogOpen}
            resetRecipientAddressForm={resetRecipientAddressForm}
            setRecipientAddressesDialogOpen={setIsRecipientDialogOpen}
            setOpenEditDialog={setOpenEditDialog}
            variant={RecipientAddressesVariant.Preview}
            onActionButtonClick={onRecipientAddressButtonClick}
            onSecondaryActionButtonClick={() => null}
          />
        )}

        {openEditDialog && (
          <EditAddressDialog
            title={t('editRecipientAddressDialog.title')}
            accentIcon={IconNames.AddressBookBold}
            isEditAddressVisible={openEditDialog}
            onCloseEditAddress={handleCloseEditAddress}
            formHandlers={formValuesPreview as unknown as UseFormReturn<AddressFormType>}
            actionButton={
              <Button
                testId={'edit-recipient-address-dialog-update-button'}
                onClick={handleSubmitPreview(handleUpdateReturnRecipientAddress)}
                disabled={!isValid}
              >
                {t('editRecipientAddressDialog.actionButton')}
              </Button>
            }
          />
        )}

        {!isSystemErrorOpen && (
          <AddToCart
            basket={basket}
            recentBasket={recentBasket}
            basketError={basketError}
            closeModalOverride={closeModalOverride}
            keepShoppingOverride={keepShoppingOverride}
          />
        )}
      </div>
    </Layout>
  );
};
